/**
 * huipil-cart.js
 * Loaded on every page. Shows multi-item discount banner for 2+ huipil items.
 * With Shopify Markets fixed pricing, checkout is native — no intercept needed.
 */

(function () {
  'use strict';

  // Regions that have a combined shipping discount (all markets now qualify)
  const REGIONS_WITH_DISCOUNT = [
    'USA', 'GBP', 'EUR', 'AUD', 'CAD', 'ASIA', 'MXN', 'ROW',
  ];

  // Comprehensive country → region map (matches HuipilPriceCron's market → region mapping)
  const COUNTRY_TO_REGION = {
    // USA
    US: 'USA',
    // GBP
    GB: 'GBP',
    // AUD
    AU: 'AUD', NZ: 'AUD',
    // CAD
    CA: 'CAD',
    // MXN (Latin America)
    MX: 'MXN', CL: 'MXN', PE: 'MXN', CO: 'MXN', AR: 'MXN',
    EC: 'MXN', UY: 'MXN', CR: 'MXN', PA: 'MXN', GT: 'MXN',
    BO: 'MXN', PY: 'MXN', VE: 'MXN', HN: 'MXN', NI: 'MXN',
    SV: 'MXN', DO: 'MXN', CU: 'MXN', JM: 'MXN', BZ: 'MXN',
    HT: 'MXN', TT: 'MXN', BB: 'MXN', LC: 'MXN', VC: 'MXN',
    GD: 'MXN', AG: 'MXN', DM: 'MXN', KN: 'MXN', BS: 'MXN',
    BR: 'MXN', 
    // EUR — EU member states
    DE: 'EUR', FR: 'EUR', IT: 'EUR', ES: 'EUR', NL: 'EUR',
    PT: 'EUR', BE: 'EUR', AT: 'EUR', SE: 'EUR', DK: 'EUR',
    FI: 'EUR', PL: 'EUR', HR: 'EUR', IE: 'EUR', CZ: 'EUR',
    RO: 'EUR', HU: 'EUR', BG: 'EUR', SK: 'EUR', SI: 'EUR',
    LT: 'EUR', LV: 'EUR', EE: 'EUR', CY: 'EUR', MT: 'EUR',
    LU: 'EUR', GR: 'EUR',
    // EUR — EEA + nearby
    NO: 'EUR', CH: 'EUR', IS: 'EUR', LI: 'EUR',
    // EUR — French overseas territories
    GP: 'EUR', MQ: 'EUR', GF: 'EUR', RE: 'EUR', YT: 'EUR',
    PM: 'EUR', BL: 'EUR', MF: 'EUR', TF: 'EUR', WF: 'EUR',
    PF: 'EUR', NC: 'EUR',
    // EUR — other European
    MC: 'EUR', AD: 'EUR', SM: 'EUR', VA: 'EUR', ME: 'EUR',
    RS: 'EUR', BA: 'EUR', AL: 'EUR', MK: 'EUR', XK: 'EUR',
    MD: 'EUR', UA: 'EUR', BY: 'EUR', TR: 'EUR',
    // ASIA (East, Southeast, South, Central Asia)
    JP: 'ASIA', SG: 'ASIA', HK: 'ASIA', KR: 'ASIA',
    MY: 'ASIA', ID: 'ASIA', PH: 'ASIA', VN: 'ASIA',
    TW: 'ASIA', CN: 'ASIA', BD: 'ASIA', LK: 'ASIA', PK: 'ASIA',
    NP: 'ASIA', MM: 'ASIA', KH: 'ASIA', LA: 'ASIA', MN: 'ASIA',
    KZ: 'ASIA', UZ: 'ASIA',
    // ROW (Rest of World — India, Middle East, Thailand, Russia, South Africa)
    IN: 'ROW', TH: 'ROW', BT: 'ROW', RU: 'ROW', ZA: 'ROW',
    AE: 'ROW', SA: 'ROW', QA: 'ROW', 
    KW: 'ROW', BH: 'ROW', OM: 'ROW', IL: 'ROW', JO: 'ROW',
  };

  function getRegion() {
    const countryCode = window.__shopifyCountry;
    if (countryCode && COUNTRY_TO_REGION[countryCode]) {
      return COUNTRY_TO_REGION[countryCode];
    }
    return 'OTHER';
  }

  function showDiscountBanner(text) {
    ['huipil-discount-banner', 'huipil-discount-banner-drawer'].forEach((id) => {
      const banner = document.getElementById(id);
      if (!banner) return;
      banner.style.display = '';
      const amountId = id === 'huipil-discount-banner'
        ? 'huipil-discount-amount'
        : 'huipil-discount-amount-drawer';
      const amountEl = document.getElementById(amountId);
      if (amountEl) amountEl.textContent = text;
    });
  }

  function hideDiscountBanner() {
    ['huipil-discount-banner', 'huipil-discount-banner-drawer'].forEach((id) => {
      const banner = document.getElementById(id);
      if (banner) banner.style.display = 'none';
    });
  }

  async function fetchCart() {
    const res = await fetch('/cart.js');
    return res.json();
  }

  let _drawerObserver = null;
  function setupDrawerReRenderWatcher(discountText) {
    if (_drawerObserver) { _drawerObserver.disconnect(); _drawerObserver = null; }
    const cartDrawer = document.getElementById('cart-drawer');
    if (!cartDrawer) return;
    _drawerObserver = new MutationObserver(() => {
      _drawerObserver.disconnect();
      if (discountText) {
        showDiscountBanner(discountText);
      } else {
        hideDiscountBanner();
      }
      setTimeout(() => {
        if (_drawerObserver) _drawerObserver.observe(cartDrawer, { childList: true, subtree: true });
      }, 50);
    });
    _drawerObserver.observe(cartDrawer, { childList: true, subtree: true });
  }

  let _initRunning = false;

  async function init() {
    if (_initRunning) return;
    _initRunning = true;
    try {
      const cart = await fetchCart().catch(() => null);
      if (!cart) return;

      // Count huipil items — identified by product_type "Blouses"
      const huipilItems = (cart.items || []).filter(
        (item) => item.product_type === 'Blouses'
      );

      if (huipilItems.length === 0) {
        hideDiscountBanner();
        return;
      }

      const region = getRegion();
      const totalHuipilQty = huipilItems.reduce((sum, item) => sum + item.quantity, 0);

      let discountText = null;
      if (totalHuipilQty >= 2 && REGIONS_WITH_DISCOUNT.includes(region)) {
        discountText = 'MultiBuy Discount applied at checkout';
        showDiscountBanner(discountText);
      } else {
        hideDiscountBanner();
      }

      setupDrawerReRenderWatcher(discountText);
    } finally {
      _initRunning = false;
    }
  }

  function setupCartChangeListener() {
    document.documentElement.addEventListener('cart:change', () => {
      setTimeout(() => init(), 150);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      init();
      setupCartChangeListener();
    });
  } else {
    init();
    setupCartChangeListener();
  }
})();
